import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
// SM-AUTH-HR: use the shared client so every call sends the login token.
// REVERT: change back to  import axios from "axios";
import axios from "./axios";
import { useUser } from "./Context/UserContext";
import Icon from "./ui/Icon";
import EmptyState from "./ui/EmptyState";
import useCountUp from "./ui/useCountUp";
import "../styles/skillHome.css";
import "../styles/TeamCompetencyDashboard.css";

export default function TeamCompetencyDashboard() {

    const { empId } = useUser();
    const [reportingEmployees, setReportingEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const history = useHistory();

    const primarySkillCounts = reportingEmployees
        .flatMap(emp => emp.skills || [])
        .filter(skill => skill)
        .reduce((acc, skill) => {
            acc[skill] = (acc[skill] || 0) + 1;
            return acc;
        }, {});

    const skillStats = Object.entries(primarySkillCounts).map(([name, count]) => ({ name, count }));
    const total = skillStats.reduce((sum, skill) => sum + skill.count, 0);
    const [filterColumn, setFilterColumn] = useState("skills");
    const [filterValue, setFilterValue] = useState("");
    const [statusFilter, setStatusFilter] = useState("All");
    const filteredEmployees = reportingEmployees.filter(emp => {
        const value = filterValue.toLowerCase();

        const matchesColumn = (() => {
            if (!value) return true;

            switch (filterColumn) {
                case "id":
                    return emp.employeeId?.toLowerCase().includes(value);
                case "name":
                    return emp.employeeName?.toLowerCase().includes(value);
                case "skills":
                    return emp.skills?.join(", ").toLowerCase().includes(value);
                case "designation":
                    return emp.designation?.toLowerCase().includes(value);
                default:
                    return true;
            }
        })();

        const matchesStatus =
            statusFilter === "All" || emp.status === statusFilter;

        return matchesColumn && matchesStatus;
    });
    const [currentPage, setCurrentPage] = useState(1);

    const itemsPerPage = 10;

    const totalPages = Math.max(1, Math.ceil(filteredEmployees.length / itemsPerPage));
    const safePage = Math.min(currentPage, totalPages);

    const currentData = filteredEmployees.slice(
        (safePage - 1) * itemsPerPage,
        safePage * itemsPerPage
    );

    useEffect(() => {
        const fetchReportingEmployees = async () => {
            if (!empId) return;
            try {
                const res = await axios.get(`/api/EmployeeSkill/reporting/${empId}`);
                setReportingEmployees(res.data);
            } catch (err) {
                if (process.env.NODE_ENV !== "production") {
                    console.error("Error fetching my skills:", err);
                }
            } finally {
                setLoading(false);
            }
        };
        fetchReportingEmployees();
    }, [empId]);

    const handleEdit = (employeeId) => {
        history.push(`/employee/${employeeId}`);
    };

    const pendingCount = reportingEmployees.filter(emp => emp.status === "Pending").length;
    const activeSkills = reportingEmployees
        .flatMap(emp => emp.skills || [])
        .filter(Boolean).length;

    const animatedTotal = useCountUp(reportingEmployees.length);
    const animatedPending = useCountUp(pendingCount);
    const animatedActive = useCountUp(activeSkills);

    return (
        <div className="skill-home-wrapper">
            <header className="page-head reveal">
                <span className="page-eyebrow">Team</span>
                <h1 className="page-title-mt">Team Competency Dashboard</h1>
                <p className="page-sub">
                    Review your reporting employees' skills and submission status.
                </p>
            </header>

            {/* KPI TILES */}
            <div className="kpi-grid team-kpi-grid">
                <div className="card-mt card-pad kpi-card reveal reveal-d1">
                    <div className="kpi-icon"><Icon name="users" size={19} /></div>
                    <div className="kpi-body">
                        <h6 className="kpi-label">Total Employees</h6>
                        <h3 className="kpi-value num">{animatedTotal}</h3>
                    </div>
                </div>

                <div className="card-mt card-pad kpi-card reveal reveal-d2">
                    <div className="kpi-icon kpi-icon-gold"><Icon name="clock" size={19} /></div>
                    <div className="kpi-body">
                        <h6 className="kpi-label">Pending Skill Entry</h6>
                        <h3 className="kpi-value num">{animatedPending}</h3>
                    </div>
                </div>

                <div className="card-mt card-pad kpi-card reveal reveal-d3">
                    <div className="kpi-icon"><Icon name="zap" size={19} /></div>
                    <div className="kpi-body">
                        <h6 className="kpi-label">Active Skills</h6>
                        <h3 className="kpi-value num">{animatedActive}</h3>
                    </div>
                </div>
            </div>

            {/* SKILL DISTRIBUTION */}
            <section className="card-mt card-pad reveal reveal-d3">
                <h5 className="card-title-mt">
                    <span className="card-title-icon"><Icon name="chart" size={16} /></span>
                    Team Skill Distribution
                </h5>
                {skillStats.length > 0 ? (
                    skillStats.map((skill) => (
                        <div key={skill.name} className="skill-bar">
                            <div className="skill-label">
                                <span className="skill-label-name">{skill.name}</span>
                                <span className="skill-points num">
                                    {skill.count} {skill.count === 1 ? "person" : "people"}
                                </span>
                            </div>
                            <div className="meter">
                                <div
                                    className="meter-fill"
                                    style={{ width: `${total > 0 ? (skill.count / total) * 100 : 0}%` }}
                                />
                            </div>
                        </div>
                    ))
                ) : (
                    <EmptyState
                        icon="chart"
                        title="No skill data yet"
                        text="Skill distribution appears once your team submits their primary skills."
                    />
                )}
            </section>

            {/* EMPLOYEE TABLE */}
            <section className="card-mt reveal reveal-d4">
                <div className="team-table-toolbar">
                    <h5 className="card-title-mt team-table-title">
                        <span className="card-title-icon"><Icon name="list" size={16} /></span>
                        Reporting Employees
                    </h5>

                    <div className="team-filters">
                        <div className="team-filter">
                            <label htmlFor="statusFilter" className="field-label">Status</label>
                            <select
                                id="statusFilter"
                                className="select-mt"
                                value={statusFilter}
                                onChange={e => {
                                    setStatusFilter(e.target.value);
                                    setCurrentPage(1);
                                }}
                            >
                                <option value="All">All Status</option>
                                <option value="Pending">Pending</option>
                                <option value="Submitted">Submitted</option>
                            </select>
                        </div>

                        <div className="team-filter">
                            <label htmlFor="filterColumn" className="field-label">Filter by</label>
                            <select
                                id="filterColumn"
                                className="select-mt"
                                value={filterColumn}
                                onChange={e => {
                                    setFilterColumn(e.target.value);
                                    setCurrentPage(1);
                                }}
                            >
                                <option value="id">Employee ID</option>
                                <option value="name">Employee Name</option>
                                <option value="skills">Skills</option>
                                <option value="designation">Designation</option>
                            </select>
                        </div>

                        <div className="team-filter team-filter-search">
                            <label htmlFor="employeeSearch" className="field-label">Search</label>
                            <div className="team-search-wrap">
                                <Icon name="search" size={16} className="team-search-icon" />
                                <input
                                    id="employeeSearch"
                                    type="text"
                                    className="input-mt team-search-input"
                                    placeholder="Search employees…"
                                    value={filterValue}
                                    onChange={e => {
                                        setFilterValue(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                />
                                {filterValue && (
                                    <button
                                        type="button"
                                        className="team-search-clear"
                                        onClick={() => setFilterValue("")}
                                        aria-label="Clear search"
                                    >
                                        <Icon name="x" size={14} />
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="table-wrap">
                    <table className="table-mt">
                        <thead>
                            <tr>
                                <th scope="col">Emp ID</th>
                                <th scope="col">Employee Name</th>
                                <th scope="col">Designation</th>
                                <th scope="col">Skills</th>
                                <th scope="col">Actions</th>
                            </tr>
                        </thead>

                        <tbody>
                            {loading ? (
                                [1, 2, 3, 4, 5].map(i => (
                                    <tr key={i}>
                                        <td colSpan="5"><div className="skeleton" style={{ height: 20 }} /></td>
                                    </tr>
                                ))
                            ) : currentData.length > 0 ? (
                                currentData.map(emp => (
                                    <tr key={emp.employeeId}>
                                        <td className="num">{emp.employeeId}</td>
                                        <td className="team-emp-name">{emp.employeeName}</td>
                                        <td>{emp.designation || "—"}</td>
                                        <td className="team-skills-cell">
                                            {emp.skills?.filter(Boolean).length > 0 ? (
                                                <span className="team-skill-tags">
                                                    {emp.skills.filter(Boolean).map((s, i) => (
                                                        <span key={i} className="badge-mt badge-neutral">{s}</span>
                                                    ))}
                                                </span>
                                            ) : (
                                                <span className="team-no-skill">No skill entered</span>
                                            )}
                                        </td>
                                        <td>
                                            {emp.status === "Pending" ? (
                                                <span className="badge-mt badge-warning">
                                                    <Icon name="clock" size={12} />
                                                    Pending
                                                </span>
                                            ) : (
                                                <button
                                                    type="button"
                                                    className="btn btn-secondary-mt btn-sm"
                                                    onClick={() => handleEdit(emp.employeeId)}
                                                >
                                                    <Icon name="edit" size={14} />
                                                    Review
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5">
                                        <EmptyState
                                            icon="search"
                                            title="No employees match this filter"
                                            text="Try a different search term or clear the filters."
                                        />
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {!loading && filteredEmployees.length > itemsPerPage && (
                    <div className="team-pagination">
                        <span className="team-pagination-info num">
                            Showing {(safePage - 1) * itemsPerPage + 1}–
                            {Math.min(safePage * itemsPerPage, filteredEmployees.length)} of {filteredEmployees.length}
                        </span>
                        <div className="team-pagination-controls">
                            <button
                                type="button"
                                className="icon-btn"
                                onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
                                disabled={safePage === 1}
                                aria-label="Previous page"
                            >
                                <Icon name="chevronLeft" size={17} />
                            </button>
                            <span className="num team-page-indicator">
                                {safePage} / {totalPages}
                            </span>
                            <button
                                type="button"
                                className="icon-btn"
                                onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
                                disabled={safePage === totalPages}
                                aria-label="Next page"
                            >
                                <Icon name="chevronRight" size={17} />
                            </button>
                        </div>
                    </div>
                )}
            </section>
        </div>
    );
}
